import { useState, useEffect } from 'react'
import { Shield, Cookie } from 'lucide-react'
import { useLocale } from '../contexts/LocaleContext'

const PRIVACY_CONSENT_KEY = 'quor_privacy_consent_accepted'
const CONSENT_VERSION = '2' // Increment when policy changes significantly

export function PrivacyConsentPopup() {
  const { tx } = useLocale()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Check if user has already accepted current version
    const acceptedVersion = localStorage.getItem(PRIVACY_CONSENT_KEY)
    if (acceptedVersion !== CONSENT_VERSION) {
      // Small delay for smooth appearance
      setTimeout(() => setIsVisible(true), 500)
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem(PRIVACY_CONSENT_KEY, CONSENT_VERSION)
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-md animate-in fade-in zoom-in duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Header - Professional slate color */}
          <div className="bg-slate-700 px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-slate-200" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{tx('Privacy & Cookies', 'פרטיות ועוגיות')}</h2>
              <p className="text-slate-300 text-sm">{tx('Israeli Privacy Protection Law', 'חוק הגנת הפרטיות')}</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="space-y-4 text-slate-600 text-sm leading-relaxed">
              <p>
                {tx('Welcome to ', 'ברוכים הבאים ל-')}<strong className="text-slate-800">Quor</strong>
              </p>

              <p>
                {tx(
                  'This website uses cookies and similar technologies for authentication, saving preferences, and improving your experience.',
                  'אתר זה משתמש בעוגיות וטכנולוגיות דומות לאימות, שמירת העדפות ושיפור החוויה שלך.'
                )}
              </p>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-slate-700 font-medium text-sm">
                  <Cookie className="w-4 h-4 inline-block mr-1 mb-0.5 text-slate-500" />
                  {tx(
                    'By continuing to browse this site, you agree to our Privacy Policy and Terms of Service.',
                    'המשך הגלישה באתר זה מהווה הסכמה למדיניות הפרטיות ותנאי השימוש שלנו.'
                  )}
                </p>
              </div>

              <p className="text-xs text-slate-400">
                {tx(
                  'In accordance with the Israeli Privacy Protection Law 5741-1981 and GDPR regulations.',
                  'בהתאם לחוק הגנת הפרטיות, התשמ"א-1981 ותקנות ה-GDPR.'
                )}
              </p>
            </div>

            {/* Action Button - Professional slate */}
            <button
              onClick={handleAccept}
              className="w-full mt-6 bg-slate-700 text-white py-3 px-6 rounded-xl font-medium hover:bg-slate-800 transition-all duration-200"
            >
              {tx('I Understand and Agree', 'אני מבין/ה ומסכים/ה')}
            </button>

            {/* Legal Links */}
            <div className="flex justify-center gap-4 mt-4 text-xs">
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-700 hover:underline">
                {tx('Privacy Policy', 'מדיניות פרטיות')}
              </a>
              <span className="text-slate-300">|</span>
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-700 hover:underline">
                {tx('Terms of Service', 'תנאי שימוש')}
              </a>
              <span className="text-slate-300">|</span>
              <a href="/accessibility" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-700 hover:underline">
                {tx('Accessibility', 'נגישות')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyConsentPopup
